import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

import { useMap } from '@/hooks/useMap';
import { computeAcreage } from '@/utils/boundary-utils';

interface BoundaryOverlayProps {
  boundary: [number, number][] | undefined;
  visible: boolean;
}

const SOURCE_ID = 'hunt-boundary';
const FILL_LAYER_ID = 'hunt-boundary-fill';
const LINE_LAYER_ID = 'hunt-boundary-line';

export function BoundaryOverlay({ boundary, visible }: BoundaryOverlayProps) {
  const map = useMap();
  const labelMarkerRef = useRef<maplibregl.Marker | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    function removeLayers() {
      if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
      if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      labelMarkerRef.current?.remove();
      labelMarkerRef.current = null;
    }

    function addLayers() {
      if (!mountedRef.current) return;
      removeLayers();

      if (!visible || !boundary || boundary.length < 3) return;

      // Convert [lat, lng] pairs → GeoJSON [lng, lat] positions
      const coordinates = boundary.map(([lat, lng]) => [lng, lat]);
      // Close the ring
      coordinates.push(coordinates[0]);

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {},
        },
      });

      map.addLayer({
        id: FILL_LAYER_ID,
        type: 'fill',
        source: SOURCE_ID,
        paint: {
          'fill-color': '#d25f1e',
          'fill-opacity': 0.08,
        },
      });

      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': '#d25f1e',
          'line-width': 2,
          'line-opacity': 0.7,
          'line-dasharray': [6, 4],
        },
      });

      // Acreage label — placed at the centroid via a DOM marker
      const acres = computeAcreage(boundary);
      const lngSum = coordinates.slice(0, -1).reduce((s, c) => s + c[0], 0);
      const latSum = coordinates.slice(0, -1).reduce((s, c) => s + c[1], 0);
      const count = coordinates.length - 1;
      const centroidLng = lngSum / count;
      const centroidLat = latSum / count;

      const el = document.createElement('div');
      el.setAttribute('data-testid', 'boundary-acreage-label');
      el.style.cssText = `
        background: rgba(30, 27, 24, 0.85);
        border: 1px solid rgba(210, 95, 30, 0.5);
        border-radius: 6px;
        padding: 3px 8px;
        font-family: 'DM Sans', system-ui, sans-serif;
        font-size: 11px;
        font-weight: 600;
        color: #e8e5e0;
        white-space: nowrap;
        pointer-events: none;
        backdrop-filter: blur(4px);
        box-shadow: 0 1px 4px rgba(0,0,0,0.4);
      `;
      // SAFETY: acres is derived from numeric coordinates — not user-controlled HTML
      el.textContent = `~${acres.toFixed(1)} acres`;

      labelMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([centroidLng, centroidLat])
        .addTo(map);
    }

    // Re-add layers when the style reloads (setStyle() wipes all sources/layers)
    mountedRef.current = true;
    map.on('style.load', addLayers);
    if (map.isStyleLoaded()) addLayers();

    return () => {
      mountedRef.current = false;
      map.off('style.load', addLayers);
      try { removeLayers(); } catch { /* map may already be destroyed */ }
    };
  }, [map, boundary, visible]);

  return null;
}
